import { getPathKey } from './searchUrl.js'

export const MIN_SEARCH_QUERY_LENGTH = 2
export const MIN_SEARCH_PREFIX_LENGTH = 4
export const FEATURED_CONTENT_MAX_AGE_DAYS = 90
export const SEARCH_CATEGORY_ORDER = [
  'Products',
  'Featured',
  'Insights',
  'News',
  'Other',
]

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

const SEARCH_URL_BASE = 'https://www.harborcapital.com'
const INTERNAL_SEARCH_HOSTS = new Set([
  'harborcapital.com',
  'www.harborcapital.com',
])
const APPROVED_RESOURCE_HOSTS = new Set([
  'assets.contentstack.io',
  'assets.harborcapital.com',
])
const DOCUMENT_PATH_PATTERN = /\.(?:csv|doc|docx|pdf|txt|xls|xlsx)$/i
const INSIGHT_LISTING_PATH_PATTERN =
  /^\/insights\/(?:author|category|product)(?:\/|$)/
const TRACKING_QUERY_PARAMS = new Set([
  'category',
  'gad_campaignid',
  'gad_source',
  'gbraid',
  'gclid',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'wtime',
])

const CONTENT_TYPE_ALIASES = {
  article: 'insight-article',
  etf: 'product',
  fund: 'product',
  insight: 'insight-article',
  insight_article: 'insight-article',
  news: 'news-article',
  news_article: 'news-article',
  pdf: 'resource',
  portfolio_model: 'portfolio-model',
  portfolio_solution: 'portfolio-solution',
  landing_page: 'landing-page',
}

const CONTENT_TYPE_CATEGORIES = {
  product: 'Products',
  'portfolio-model': 'Products',
  'portfolio-solution': 'Products',
  'insight-article': 'Insights',
  'news-article': 'News',
}

export const normalizeSearchQuery = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')

export const isSearchQueryEligible = (value) =>
  normalizeSearchQuery(value).length >= MIN_SEARCH_QUERY_LENGTH

export const tokenizeSearchText = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .toLowerCase()
    .match(/[a-z0-9]+/g) || []

const normalizeSearchText = (value) => tokenizeSearchText(value).join(' ')

export const collectSearchStrings = (value, maxDepth = 4) => {
  const strings = []

  const collect = (currentValue, depth) => {
    if (
      depth > maxDepth ||
      currentValue === null ||
      currentValue === undefined
    ) {
      return
    }

    if (typeof currentValue === 'string' || typeof currentValue === 'number') {
      strings.push(String(currentValue))
      return
    }

    if (Array.isArray(currentValue)) {
      currentValue.forEach((entry) => collect(entry, depth + 1))
      return
    }

    if (typeof currentValue === 'object') {
      Object.values(currentValue).forEach((entry) => collect(entry, depth + 1))
    }
  }

  collect(value, 0)
  return strings
}

const includesAllTokens = (value, queryTokens) => {
  if (!queryTokens.length) return true
  const valueTokens = new Set(tokenizeSearchText(value))
  return queryTokens.every((token) => valueTokens.has(token))
}

const includesAllTokenPrefixes = (value, queryTokens) => {
  if (!queryTokens.length) return true
  const valueTokens = tokenizeSearchText(value)

  return queryTokens.every((queryToken) =>
    valueTokens.some(
      (valueToken) =>
        valueToken === queryToken ||
        (queryToken.length >= MIN_SEARCH_PREFIX_LENGTH &&
          valueToken.startsWith(queryToken))
    )
  )
}

export const matchesSearchQuery = (value, searchTerms) => {
  const queryTokens = tokenizeSearchText(searchTerms)
  return includesAllTokenPrefixes(value, queryTokens)
}

const removeTrackingParams = (url) => {
  Array.from(url.searchParams.keys()).forEach((key) => {
    const normalizedKey = key.toLowerCase()
    if (
      TRACKING_QUERY_PARAMS.has(normalizedKey) ||
      normalizedKey.startsWith('utm_')
    ) {
      url.searchParams.delete(key)
    }
  })
}

const normalizePathname = (pathname, { lowercase = false } = {}) => {
  const normalized = String(pathname || '/').replace(/\/{2,}/g, '/') || '/'
  const withLeadingSlash = normalized.startsWith('/')
    ? normalized
    : `/${normalized}`
  const withoutTrailingSlash =
    withLeadingSlash === '/'
      ? withLeadingSlash
      : withLeadingSlash.replace(/\/+$/, '')

  return lowercase ? withoutTrailingSlash.toLowerCase() : withoutTrailingSlash
}

export const getSearchUrlData = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null

  try {
    const rawValue = value.trim()
    const parsedUrl = new URL(rawValue, SEARCH_URL_BASE)
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) return null

    const hostname = parsedUrl.hostname.toLowerCase()
    const isInternal = INTERNAL_SEARCH_HOSTS.has(hostname)
    const isDocument = DOCUMENT_PATH_PATTERN.test(parsedUrl.pathname)
    const isApprovedResource =
      isDocument && (isInternal || APPROVED_RESOURCE_HOSTS.has(hostname))

    if (!isInternal && !isApprovedResource) return null

    removeTrackingParams(parsedUrl)

    if (isInternal) {
      const pathKey = normalizePathname(parsedUrl.pathname, { lowercase: true })
      const navigationPath = isDocument
        ? normalizePathname(parsedUrl.pathname)
        : pathKey
      const query = parsedUrl.searchParams.toString()
      const navigationUrl = `${navigationPath}${query ? `?${query}` : ''}${
        parsedUrl.hash || ''
      }`

      return {
        canonicalKey: pathKey,
        isDocument,
        isInternal: true,
        navigationUrl,
        pathKey,
      }
    }

    const resourcePath = normalizePathname(parsedUrl.pathname)
    const canonicalKey =
      `${parsedUrl.protocol}//${hostname}${resourcePath}`.toLowerCase()
    const query = parsedUrl.searchParams.toString()
    const navigationUrl = `${parsedUrl.protocol}//${hostname}${resourcePath}${
      query ? `?${query}` : ''
    }${parsedUrl.hash || ''}`

    return {
      canonicalKey,
      isDocument: true,
      isInternal: false,
      navigationUrl,
      pathKey: resourcePath,
    }
  } catch {
    return null
  }
}

const getResultDocument = (result) => result?.document || {}
const getDerivedData = (result) =>
  getResultDocument(result).derivedStructData || {}
const getStructuredData = (result) => getResultDocument(result).structData || {}

export const getSearchResultUrl = (result) => {
  const document = getResultDocument(result)
  const derivedData = getDerivedData(result)
  return (
    result?.url ||
    result?.canonicalUrl ||
    derivedData.link ||
    document.url ||
    document.link ||
    ''
  )
}

export const getSearchResultTitle = (result) => {
  const document = getResultDocument(result)
  const derivedData = getDerivedData(result)
  const structuredData = getStructuredData(result)
  return String(
    result?.title ||
      structuredData.title ||
      derivedData.title ||
      document.title ||
      document.name ||
      ''
  ).trim()
}

export const getSearchResultSnippet = (result) => {
  const derivedData = getDerivedData(result)
  const snippet = Array.isArray(derivedData.snippets)
    ? derivedData.snippets[0]?.snippet
    : derivedData.snippets?.snippet
  return String(result?.snippet || snippet || '').trim()
}

const normalizeContentType = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
  return CONTENT_TYPE_ALIASES[normalized] || normalized
}

export const getSearchResultContentType = (result) => {
  const structuredData = getStructuredData(result)
  const derivedData = getDerivedData(result)
  const explicitType = normalizeContentType(
    result?.contentType ||
      result?.content_type ||
      structuredData.contentType ||
      structuredData.content_type ||
      derivedData.contentType ||
      derivedData.content_type ||
      result?.searchMetadata?.contentType
  )

  if (explicitType) return explicitType

  const urlData = getSearchUrlData(getSearchResultUrl(result))
  const pathKey = urlData?.pathKey || ''
  if (urlData?.isDocument) return 'resource'
  if (/^\/(?:etf|etfs|fund|funds|products)(?:\/|$)/.test(pathKey)) {
    return 'product'
  }
  if (
    pathKey === '/portfolio-solutions' ||
    pathKey === '/portfolio-solutions/all'
  ) {
    return 'portfolio-solution'
  }

  return 'other'
}

export const getSearchResultAccessState = (result) => {
  const structuredData = getStructuredData(result)
  return String(
    result?.accessState ||
      result?.access_state ||
      structuredData.accessState ||
      structuredData.access_state ||
      result?.searchMetadata?.accessState ||
      'public'
  )
    .trim()
    .toLowerCase()
}

export const getSearchResultCanonicalKey = (result) =>
  result?.searchMetadata?.canonicalKey ||
  getSearchUrlData(result?.canonicalUrl)?.canonicalKey ||
  getSearchUrlData(getSearchResultUrl(result))?.canonicalKey ||
  null

const getExplicitMatchedFields = (result) => {
  const structuredData = getStructuredData(result)
  const matchedFields =
    result?.matchedFields ||
    result?.matched_fields ||
    structuredData.matchedFields ||
    structuredData.matched_fields ||
    []
  return Array.isArray(matchedFields) ? matchedFields : []
}

const getExplicitMatchTier = (matchedFields) => {
  const normalizedFields = matchedFields.map((field) =>
    String(field || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
  )

  if (
    normalizedFields.some((field) =>
      ['alias', 'aliases', 'ticker', 'tickers'].includes(field)
    )
  ) {
    return 1
  }
  if (
    normalizedFields.some((field) =>
      [
        'benchmark',
        'category',
        'description',
        'direct',
        'herotext',
        'keywords',
        'objective',
        'producttype',
        'subadviser',
        'summary',
      ].includes(field)
    )
  ) {
    return 5
  }
  if (normalizedFields.some((field) => ['body', 'content'].includes(field))) {
    return 7
  }
  if (
    normalizedFields.some((field) =>
      ['reference', 'referenceids', 'referencetext'].includes(field)
    )
  ) {
    return 8
  }

  return null
}

const getSearchFieldValues = (result) => {
  const document = getResultDocument(result)
  const structuredData = getStructuredData(result)
  const derivedData = getDerivedData(result)
  const searchFields = result?.searchFields || structuredData.searchFields || {}
  const title = getSearchResultTitle(result)
  const aliases = collectSearchStrings(
    searchFields.aliases || result?.aliases || structuredData.aliases || []
  )
  const tickers = collectSearchStrings(
    searchFields.tickers ||
      searchFields.ticker ||
      result?.ticker ||
      structuredData.ticker ||
      []
  )
  const direct = collectSearchStrings([
    title,
    aliases,
    tickers,
    searchFields.direct,
    result?.summary,
    result?.keywords,
    structuredData.summary,
    structuredData.keywords,
    structuredData.description,
    document.description,
    derivedData.snippets,
  ])
  const body = collectSearchStrings([
    searchFields.body,
    result?.body,
    structuredData.body,
    document.body,
    document.content,
    document.text,
  ])
  const reference = collectSearchStrings([
    searchFields.referenceText,
    result?.referenceText,
    structuredData.referenceText,
    structuredData.reference_text,
  ])

  return { aliases, body, direct, reference, tickers, title }
}

const parseSearchDate = (value) => {
  if (value instanceof Date) {
    const timestamp = value.getTime()
    return Number.isFinite(timestamp) ? timestamp : null
  }

  if (typeof value !== 'string' && typeof value !== 'number') return null
  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

const getExplicitSearchDates = (result) => {
  const structuredData = getStructuredData(result)
  const searchMetadata = result?.searchMetadata || {}
  const updatedAt = parseSearchDate(
    searchMetadata.updatedAt ||
      result?.updatedAt ||
      result?.updated_at ||
      structuredData.updatedAt ||
      structuredData.updated_at
  )
  const publishedAt = parseSearchDate(
    searchMetadata.publishedAt ||
      result?.publishedAt ||
      result?.published_at ||
      result?.date ||
      structuredData.publishedAt ||
      structuredData.published_at ||
      structuredData.date
  )

  return { publishedAt, updatedAt }
}

const getReferenceTimestamp = (referenceDate) => {
  const timestamp = parseSearchDate(referenceDate)
  return timestamp === null ? Date.now() : timestamp
}

export const getSearchResultFreshnessTimestamp = (
  result,
  referenceDate = new Date()
) => {
  const { publishedAt, updatedAt } = getExplicitSearchDates(result)
  const explicitDates = [publishedAt, updatedAt].filter(Number.isFinite)
  if (explicitDates.length) return Math.max(...explicitDates)

  const documentAgeHours = Number(result?.rankSignals?.documentAge)
  if (!Number.isFinite(documentAgeHours) || documentAgeHours < 0) return null

  return (
    getReferenceTimestamp(referenceDate) -
    documentAgeHours * 60 * 60 * 1000
  )
}

export const hasCompleteSearchPrefixMatch = (result, searchTerms) => {
  const queryTokens = tokenizeSearchText(searchTerms)
  if (!queryTokens.length) return false

  const fields = getSearchFieldValues(result)
  return [
    fields.title,
    fields.aliases,
    fields.tickers,
    fields.direct,
    fields.body,
    fields.reference,
  ].some((value) => includesAllTokenPrefixes(value, queryTokens))
}

export const hasKeywordSearchMatch = (result, searchTerms) => {
  if (hasCompleteSearchPrefixMatch(result, searchTerms)) return true
  return Number(result?.rankSignals?.keywordSimilarityScore) > 0
}

export const hasSemanticSearchMatch = (result) =>
  result?.searchMetadata?.semanticMatch === true ||
  Number(result?.rankSignals?.semanticSimilarityScore) > 0

export const isFeaturedSearchResult = (
  result,
  searchTerms,
  {
    maxAgeDays = FEATURED_CONTENT_MAX_AGE_DAYS,
    referenceDate = new Date(),
  } = {}
) => {
  if (getSearchResultContentType(result) !== 'landing-page') return false

  const referenceTimestamp = getReferenceTimestamp(referenceDate)
  const { publishedAt, updatedAt } = getExplicitSearchDates(result)
  const landingDates = [publishedAt, updatedAt].filter(Number.isFinite)
  if (!landingDates.length) return false

  const latestLandingDate = Math.max(...landingDates)
  const age = referenceTimestamp - latestLandingDate
  const isWithinFeaturedWindow =
    age >= 0 && age <= maxAgeDays * MILLISECONDS_PER_DAY

  return (
    isWithinFeaturedWindow &&
    (hasKeywordSearchMatch(result, searchTerms) ||
      hasSemanticSearchMatch(result))
  )
}

export const getSearchResultCategory = (
  result,
  searchTerms = '',
  options = {}
) => {
  const contentType = getSearchResultContentType(result)
  const baseCategory = CONTENT_TYPE_CATEGORIES[contentType] || 'Other'
  if (baseCategory === 'Products') return baseCategory

  return isFeaturedSearchResult(result, searchTerms, options)
    ? 'Featured'
    : baseCategory
}

const findOrderedTokenSpan = (value, queryTokens) => {
  const valueTokens = tokenizeSearchText(value)
  let minimumSpan = null

  for (let startIndex = 0; startIndex < valueTokens.length; startIndex += 1) {
    if (valueTokens[startIndex] !== queryTokens[0]) continue
    let queryIndex = 1

    if (queryIndex === queryTokens.length) return 0

    for (let index = startIndex + 1; index < valueTokens.length; index += 1) {
      if (valueTokens[index] !== queryTokens[queryIndex]) continue
      queryIndex += 1
      if (queryIndex === queryTokens.length) {
        const span = index - startIndex - queryTokens.length + 1
        minimumSpan = minimumSpan === null ? span : Math.min(minimumSpan, span)
        break
      }
    }
  }

  return minimumSpan
}

const hasContiguousTokens = (value, queryTokens) => {
  if (!queryTokens.length) return false
  const valueTokens = tokenizeSearchText(value)
  const finalStartIndex = valueTokens.length - queryTokens.length

  for (let startIndex = 0; startIndex <= finalStartIndex; startIndex += 1) {
    const matches = queryTokens.every(
      (token, queryIndex) => valueTokens[startIndex + queryIndex] === token
    )
    if (matches) return true
  }

  return false
}

export const getSearchResultRank = (result, searchTerms) => {
  const query = normalizeSearchQuery(searchTerms)
  const normalizedQuery = normalizeSearchText(query)
  const queryTokens = tokenizeSearchText(query)
  const fields = getSearchFieldValues(result)
  const normalizedAliases = fields.aliases.map(normalizeSearchText)
  const normalizedTickers = fields.tickers.map(normalizeSearchText)
  const titleTokens = tokenizeSearchText(fields.title)
  const titleTokenSet = new Set(titleTokens)
  const titleMatches = queryTokens.filter((token) => titleTokenSet.has(token))
  const matchedFields = getExplicitMatchedFields(result)

  if (
    normalizedQuery &&
    [...normalizedTickers, ...normalizedAliases].includes(normalizedQuery)
  ) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 1 }
  }

  if (hasContiguousTokens(fields.title, queryTokens)) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 2 }
  }

  const orderedSpan = findOrderedTokenSpan(fields.title, queryTokens)
  if (queryTokens.length && orderedSpan !== null) {
    return { coverage: 1, matchedFields, proximity: orderedSpan, tier: 3 }
  }

  if (queryTokens.length && includesAllTokens(fields.title, queryTokens)) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 4 }
  }

  if (queryTokens.length && includesAllTokens(fields.direct, queryTokens)) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 5 }
  }

  if (
    queryTokens.length &&
    includesAllTokenPrefixes(fields.title, queryTokens)
  ) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 4 }
  }

  if (
    queryTokens.length &&
    includesAllTokenPrefixes(fields.direct, queryTokens)
  ) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 5 }
  }

  if (titleMatches.length) {
    return {
      coverage: titleMatches.length / queryTokens.length,
      matchedFields,
      proximity: 0,
      tier: 6,
    }
  }

  if (queryTokens.length && includesAllTokens(fields.body, queryTokens)) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 7 }
  }

  if (
    queryTokens.length &&
    includesAllTokenPrefixes(fields.body, queryTokens)
  ) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 7 }
  }

  if (queryTokens.length && includesAllTokens(fields.reference, queryTokens)) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 8 }
  }

  if (
    queryTokens.length &&
    includesAllTokenPrefixes(fields.reference, queryTokens)
  ) {
    return { coverage: 1, matchedFields, proximity: 0, tier: 8 }
  }

  const explicitMatchTier = getExplicitMatchTier(matchedFields)
  if (
    explicitMatchTier !== null &&
    hasKeywordSearchMatch(result, searchTerms)
  ) {
    return {
      coverage: explicitMatchTier === 1 ? 1 : 0,
      matchedFields,
      proximity: 0,
      tier: explicitMatchTier,
    }
  }

  return { coverage: 0, matchedFields, proximity: 0, tier: 9 }
}

export const hasDirectSearchMatch = (result, searchTerms) =>
  getSearchResultRank(result, searchTerms).tier < 9

export const filterCatalogSearchResults = (
  results,
  searchTerms,
  localCatalogResults
) => {
  const localCanonicalKeys = new Set(
    (Array.isArray(localCatalogResults) ? localCatalogResults : [])
      .map(getSearchResultCanonicalKey)
      .filter(Boolean)
  )

  return (Array.isArray(results) ? results : []).filter((result) => {
    const canonicalKey = getSearchResultCanonicalKey(result)
    const isProduct =
      CONTENT_TYPE_CATEGORIES[getSearchResultContentType(result)] === 'Products'
    if (!isProduct) return true

    const isFundOrEtfDetail = /^\/(?:etf|fund)\/[^/]+$/.test(
      canonicalKey || ''
    )

    return isFundOrEtfDetail
      ? localCanonicalKeys.has(canonicalKey) ||
          hasKeywordSearchMatch(result, searchTerms)
      : hasKeywordSearchMatch(result, searchTerms)
  })
}

const compareSearchRanks = (firstRank, secondRank) =>
  firstRank.tier - secondRank.tier ||
  firstRank.proximity - secondRank.proximity ||
  secondRank.coverage - firstRank.coverage

const compareFreshness = (firstTimestamp, secondTimestamp) => {
  if (firstTimestamp === secondTimestamp) return 0
  if (firstTimestamp === null) return 1
  if (secondTimestamp === null) return -1
  return secondTimestamp - firstTimestamp
}

export const rankSearchResults = (
  results,
  searchTerms,
  {
    maxAgeDays = FEATURED_CONTENT_MAX_AGE_DAYS,
    referenceDate = new Date(),
  } = {}
) =>
  (Array.isArray(results) ? results : [])
    .map((result, upstreamIndex) => ({
      canonicalKey: getSearchResultCanonicalKey(result) || '',
      category: getSearchResultCategory(result, searchTerms, {
        maxAgeDays,
        referenceDate,
      }),
      freshnessTimestamp: getSearchResultFreshnessTimestamp(
        result,
        referenceDate
      ),
      rank: getSearchResultRank(result, searchTerms),
      result,
      upstreamIndex,
    }))
    .sort((first, second) => {
      const categoryComparison =
        SEARCH_CATEGORY_ORDER.indexOf(first.category) -
        SEARCH_CATEGORY_ORDER.indexOf(second.category)
      if (categoryComparison) return categoryComparison

      const relevanceComparison = compareSearchRanks(first.rank, second.rank)
      const freshnessComparison = compareFreshness(
        first.freshnessTimestamp,
        second.freshnessTimestamp
      )
      const primaryComparison =
        first.category === 'Products'
          ? relevanceComparison || freshnessComparison
          : freshnessComparison || relevanceComparison

      return (
        primaryComparison ||
        first.canonicalKey.localeCompare(second.canonicalKey) ||
        first.upstreamIndex - second.upstreamIndex
      )
    })
    .map(({ result }) => result)

export const groupSearchResults = (results, searchTerms, options = {}) => {
  const resolvedOptions = {
    ...options,
    referenceDate: options.referenceDate || new Date(),
  }
  const groups = new Map(
    SEARCH_CATEGORY_ORDER.map((category) => [category, []])
  )

  rankSearchResults(results, searchTerms, resolvedOptions).forEach((result) => {
    const category = getSearchResultCategory(
      result,
      searchTerms,
      resolvedOptions
    )
    groups.get(category).push(result)
  })

  return SEARCH_CATEGORY_ORDER.filter(
    (category) => groups.get(category).length
  ).map((category) => ({ category, results: groups.get(category) }))
}

export const canViewerAccessSearchResult = (
  result,
  {
    authResolved = false,
    isAuthenticated = false,
    isInstitutional = false,
    privatePaths = null,
  } = {}
) => {
  const accessState = getSearchResultAccessState(result)
  if (accessState === 'institutional') {
    return authResolved && isAuthenticated && isInstitutional
  }

  const resultPath = getPathKey(getSearchResultUrl(result))
  if (resultPath && privatePaths?.has(resultPath)) {
    return authResolved && isAuthenticated && isInstitutional
  }

  return true
}

const mergeDuplicateResults = (preferred, additional) => {
  const preferredDocument = getResultDocument(preferred)
  const additionalDocument = getResultDocument(additional)
  const preferredDerived = getDerivedData(preferred)
  const additionalDerived = getDerivedData(additional)
  const preferredStructured = getStructuredData(preferred)
  const additionalStructured = getStructuredData(additional)

  const merged = {
    ...additional,
    ...preferred,
    document: {
      ...additionalDocument,
      ...preferredDocument,
      derivedStructData: {
        ...additionalDerived,
        ...preferredDerived,
      },
      structData: {
        ...additionalStructured,
        ...preferredStructured,
      },
    },
  }

  const preferredContentType = getSearchResultContentType(preferred)
  const additionalContentType = getSearchResultContentType(additional)
  const contentType = [preferredContentType, additionalContentType].includes(
    'portfolio-model'
  )
    ? 'portfolio-model'
    : preferredContentType
  const accessState = [
    getSearchResultAccessState(preferred),
    getSearchResultAccessState(additional),
  ].includes('institutional')
    ? 'institutional'
    : getSearchResultAccessState(preferred)

  return {
    ...merged,
    accessState,
    contentType,
    document: {
      ...merged.document,
      structData: {
        ...merged.document.structData,
        accessState,
        contentType,
      },
    },
    searchMetadata: {
      ...(merged.searchMetadata || {}),
      accessState,
      category: CONTENT_TYPE_CATEGORIES[contentType] || 'Other',
      contentType,
    },
  }
}

const normalizeResult = (
  result,
  {
    availablePagePaths = null,
    contentMetadataByPath = null,
    contentTypeByPath = null,
    modelDetailPaths = null,
    resolveCanonicalPath,
  } = {}
) => {
  const originalUrlData = getSearchUrlData(getSearchResultUrl(result))
  if (!originalUrlData) return null
  const document = getResultDocument(result)
  const derivedData = getDerivedData(result)
  const structuredData = getStructuredData(result)
  const explicitCanonicalUrl =
    result?.canonicalUrl ||
    structuredData.canonicalUrl ||
    structuredData.canonical_url
  const explicitCanonicalData = getSearchUrlData(explicitCanonicalUrl)

  const resolvedPath =
    originalUrlData.isInternal && typeof resolveCanonicalPath === 'function'
      ? resolveCanonicalPath(originalUrlData.pathKey)
      : null
  const urlData = resolvedPath
    ? getSearchUrlData(resolvedPath)
    : originalUrlData
  if (!urlData) return null
  const canonicalData = resolvedPath
    ? urlData
    : explicitCanonicalData || urlData

  if (
    urlData.isInternal &&
    !urlData.isDocument &&
    availablePagePaths &&
    !availablePagePaths.has(urlData.pathKey)
  ) {
    return null
  }

  const recordPathKey = canonicalData.pathKey || urlData.pathKey
  const isInsightListing =
    recordPathKey === '/insights' ||
    INSIGHT_LISTING_PATH_PATTERN.test(recordPathKey)
  const pathMetadata = contentMetadataByPath?.get(recordPathKey) || {}
  const inferredContentType = getSearchResultContentType(result)
  const isProductContent = [
    'product',
    'portfolio-model',
    'portfolio-solution',
  ].includes(inferredContentType)
  const mappedContentType = isInsightListing
    ? 'other'
    : isProductContent
      ? inferredContentType
      : pathMetadata.contentType || contentTypeByPath?.get(recordPathKey)
  const contentType = normalizeContentType(
    mappedContentType || inferredContentType
  )
  const isModelDetail = Boolean(
    modelDetailPaths?.has(recordPathKey) ||
    modelDetailPaths?.has(urlData.pathKey)
  )
  const normalizedContentType = isModelDetail
    ? 'portfolio-model'
    : contentType || 'other'
  const accessState =
    normalizedContentType === 'portfolio-model'
      ? 'institutional'
      : getSearchResultAccessState(result)
  const title = getSearchResultTitle(result)
  const snippet = getSearchResultSnippet(result)
  const matchedFields = getExplicitMatchedFields(result)
  const snippets = snippet
    ? [{ snippet }]
    : Array.isArray(derivedData.snippets)
      ? derivedData.snippets
      : []

  return {
    ...result,
    id: result?.id || document.id || canonicalData.canonicalKey,
    accessState,
    canonicalUrl: canonicalData.canonicalKey,
    contentType: normalizedContentType,
    matchedFields,
    snippet,
    title,
    url: urlData.navigationUrl,
    document: {
      ...document,
      derivedStructData: {
        ...derivedData,
        link: urlData.navigationUrl,
        snippets,
        title,
      },
      structData: {
        ...structuredData,
        accessState,
        canonicalUrl: canonicalData.canonicalKey,
        contentType: normalizedContentType,
        matchedFields,
      },
    },
    searchMetadata: {
      ...(result?.searchMetadata || {}),
      accessState,
      canonicalKey: canonicalData.canonicalKey,
      category: CONTENT_TYPE_CATEGORIES[normalizedContentType] || 'Other',
      contentType: normalizedContentType,
      publishedAt:
        pathMetadata.publishedAt || result?.searchMetadata?.publishedAt || null,
      updatedAt:
        pathMetadata.updatedAt || result?.searchMetadata?.updatedAt || null,
    },
  }
}

export const normalizeSearchResults = (results, options = {}) => {
  const uniqueResults = new Map()
  const sourceResults = Array.isArray(results) ? results : []

  sourceResults.forEach((result) => {
    const normalizedResult = normalizeResult(result, options)
    const canonicalKey = getSearchResultCanonicalKey(normalizedResult)
    if (!normalizedResult || !canonicalKey) return

    const existingResult = uniqueResults.get(canonicalKey)
    uniqueResults.set(
      canonicalKey,
      existingResult
        ? mergeDuplicateResults(existingResult, normalizedResult)
        : normalizedResult
    )
  })

  return Array.from(uniqueResults.values())
}

export const mergeSearchResultPages = (currentResults, nextResults) => {
  const merged = new Map()
  const allResults = [
    ...(Array.isArray(currentResults) ? currentResults : []),
    ...(Array.isArray(nextResults) ? nextResults : []),
  ]

  allResults.forEach((result) => {
    const canonicalKey = getSearchResultCanonicalKey(result)
    if (!canonicalKey) return
    const existingResult = merged.get(canonicalKey)
    merged.set(
      canonicalKey,
      existingResult ? mergeDuplicateResults(existingResult, result) : result
    )
  })

  return Array.from(merged.values())
}

export const getUnrequestedPageToken = (pageToken, requestedPageTokens) => {
  if (typeof pageToken !== 'string' || !pageToken.trim()) return null
  return requestedPageTokens?.has(pageToken) ? null : pageToken
}
